import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import Layout from '../../components/Layout';
import { Plus, Edit, Trash2, Package, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Badge } from '../../components/ui/Badge';

export default function AdminPackages() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_monthly: 0,
    price_annual: 0,
    trial_days: 14,
    has_trial: true,
    features: [],
    limits: {},
    stripe_price_id_monthly: '',
    stripe_price_id_annual: '',
    stripe_product_id: '',
    is_active: true,
    is_featured: false,
    sort_order: 0,
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/packages');
      setPackages(response.data.packages || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load packages');
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg.id);
      setFormData({
        name: pkg.name || '',
        description: pkg.description || '',
        price_monthly: parseFloat(pkg.price_monthly) || 0,
        price_annual: parseFloat(pkg.price_annual) || 0,
        trial_days: pkg.trial_days || 14,
        has_trial: pkg.has_trial !== false,
        features: Array.isArray(pkg.features) ? pkg.features : [],
        limits: typeof pkg.limits === 'object' ? pkg.limits : {},
        stripe_price_id_monthly: pkg.stripe_price_id_monthly || '',
        stripe_price_id_annual: pkg.stripe_price_id_annual || '',
        stripe_product_id: pkg.stripe_product_id || '',
        is_active: pkg.is_active !== false,
        is_featured: pkg.is_featured || false,
        sort_order: pkg.sort_order || 0,
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price_monthly: 0,
        price_annual: 0,
        trial_days: 14,
        has_trial: true,
        features: [],
        limits: {},
        stripe_price_id_monthly: '',
        stripe_price_id_annual: '',
        stripe_product_id: '',
        is_active: true,
        is_featured: false,
        sort_order: 0,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPackage(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleAddFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [...prev.features, ''],
    }));
  };

  const handleUpdateFeature = (index, value) => {
    setFormData((prev) => {
      const newFeatures = [...prev.features];
      newFeatures[index] = value;
      return { ...prev, features: newFeatures };
    });
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (editingPackage) {
        await api.put(`/admin/packages/${editingPackage}`, formData);
      } else {
        await api.post('/admin/packages', formData);
      }
      await fetchPackages();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;

    try {
      await api.delete(`/admin/packages/${id}`);
      await fetchPackages();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete package');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Subscription Packages
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage subscription packages available to tenants
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => handleOpenModal()}
            >
              <Plus className="h-5 w-5 mr-2" />
              New Package
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)] text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
            {error}
          </div>
        )}

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`border-2 ${
                pkg.is_featured ? 'border-[var(--ds-accent-primary)]' : 'border-[var(--ds-border-subtle)]'
              }`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <Package className="h-8 w-8 text-[var(--ds-accent-primary)] mr-3" />
                    <div>
                      <h3 className="text-h1 font-bold text-[var(--ds-text-primary)]">
                        {pkg.name}
                      </h3>
                      {pkg.is_featured && (
                        <Badge variant="warning" className="mt-1">Featured</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {pkg.is_active ? (
                      <CheckCircle className="h-5 w-5 text-[var(--ds-status-success)]" />
                    ) : (
                      <XCircle className="h-5 w-5 text-[var(--ds-text-muted)]" />
                    )}
                  </div>
                </div>

                <p className="text-body text-[var(--ds-text-secondary)] mb-4">
                  {pkg.description || 'No description'}
                </p>

                <div className="mb-4">
                  <div className="flex justify-between text-body mb-1">
                    <span className="text-[var(--ds-text-muted)]">Monthly:</span>
                    <span className="font-semibold text-[var(--ds-text-primary)]">
                      £{parseFloat(pkg.price_monthly).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-body">
                    <span className="text-[var(--ds-text-muted)]">Annual:</span>
                    <span className="font-semibold text-[var(--ds-text-primary)]">
                      £{parseFloat(pkg.price_annual).toFixed(2)}
                    </span>
                  </div>
                  {pkg.has_trial && (
                    <div className="flex justify-between text-body mt-1">
                      <span className="text-[var(--ds-text-muted)]">Trial:</span>
                      <span className="font-semibold text-[var(--ds-accent-primary)]">
                        {pkg.trial_days} days
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 mt-4">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleOpenModal(pkg)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 inline mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(pkg.id)}
                    className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-[var(--ds-text-muted)] mx-auto mb-4" />
            <p className="text-body text-[var(--ds-text-muted)]">No packages found</p>
            <Button
              variant="primary"
              onClick={() => handleOpenModal()}
              className="mt-4"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create First Package
            </Button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <h2 className="text-h1 font-bold text-[var(--ds-text-primary)] mb-6">
                {editingPackage ? 'Edit Package' : 'Create Package'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Sort Order
                    </label>
                    <Input
                      type="number"
                      name="sort_order"
                      value={formData.sort_order}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                    Description
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Monthly Price (£) *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      name="price_monthly"
                      value={formData.price_monthly}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Annual Price (£) *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      name="price_annual"
                      value={formData.price_annual}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Trial Days
                    </label>
                    <Input
                      type="number"
                      name="trial_days"
                      value={formData.trial_days}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="has_trial"
                        checked={formData.has_trial}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-meta text-[var(--ds-text-primary)]">Has Trial</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Features
                  </label>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Input
                          type="text"
                          value={feature}
                          onChange={(e) => handleUpdateFeature(index, e.target.value)}
                          placeholder="Feature description"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFeature(index)}
                          className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                        >
                          <XCircle className="h-5 w-5" />
                        </Button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddFeature}
                      className="text-sm text-orange-600 dark:text-orange-500 hover:underline"
                    >
                      + Add Feature
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Stripe Monthly Price ID
                    </label>
                    <Input
                      type="text"
                      name="stripe_price_id_monthly"
                      value={formData.stripe_price_id_monthly}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Stripe Annual Price ID
                    </label>
                    <Input
                      type="text"
                      name="stripe_price_id_annual"
                      value={formData.stripe_price_id_annual}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                      Stripe Product ID
                    </label>
                    <Input
                      type="text"
                      name="stripe_product_id"
                      value={formData.stripe_product_id}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-meta text-[var(--ds-text-primary)]">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span className="text-meta text-[var(--ds-text-primary)]">Featured</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : editingPackage ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}

